<template>
  <div
    class="softphone-widget d-flex flex-column"
    data-cy="softphone-widget"
  >
    <!--
      The status, and beside it the way out of it.

      Go offline sits up here rather than down with the call controls: those are about the call in
      front of the agent, and this is about the shift - it is how they stop taking calls from
      wherever the panel is open, instead of going back to the campaigns list for it. Small and
      quiet, like Decline and Mute, because it is not the thing to do while a call is on.

      Positioned off the badge's right rather than laid out beside it, so the badge stays centred
      on the panel whatever the button next to it says.
    -->
    <div class="softphone-widget__status">
      <span
        class="softphone-widget__badge"
        :class="statusClass"
        data-cy="softphone-status"
      >
        {{ softphoneStatusLabel }}
      </span>

      <b-button
        class="softphone-widget__go-offline"
        type="button"
        size="sm"
        variant="outline-secondary"
        :disabled="!canGoOffline"
        :title="goOfflineTitle"
        data-cy="softphone-go-offline"
        @click="onGoOffline"
      >
        <b-spinner
          v-if="isGoingOffline"
          small
        />
        <span v-else>Go offline</span>
      </b-button>
    </div>

    <!--
      One line, and everything the agent needs before the call itself: what the caller is calling
      about, the language they will speak, and how long the call has to run to be billable. The
      category is what is centred - on its own, as if the rest were not there - and the language
      and the duration hang off its right in a smaller, muted type, so the subject sits where the
      status badge above it does and the eye lands on it first. No punctuation between them -
      short terms set apart by weight read faster than a comma list. The campaign name and its
      terms are the agent's own bookkeeping, not something they read mid-call.

      ONE line, always. A category too long for it is set smaller rather than wrapped - see
      `fitCategory` - and past the smallest size it is cut with an ellipsis.

      Nothing but a dash until there is a category: a duration with no subject would be a number
      hanging in the air.
    -->
    <div class="text-center mt-3">
      <div
        ref="campaign"
        class="softphone-widget__campaign"
        data-cy="softphone-campaign"
      >
        <template v-if="liveCampaignCategoryName">
          <div
            ref="campaignLine"
            class="softphone-widget__campaign-line"
          >
            <span
              ref="campaignName"
              class="softphone-widget__campaign-name"
              data-cy="softphone-campaign-category"
              :title="liveCampaignCategoryName"
            >{{ liveCampaignCategoryName }}</span>
            <span
              v-if="campaignDetails.length"
              ref="campaignDetails"
              class="softphone-widget__campaign-details"
              data-cy="softphone-campaign-details"
            >
              <span
                v-for="detail in campaignDetails"
                :key="detail.key"
                class="softphone-widget__campaign-detail text-muted"
                :data-cy="`softphone-campaign-${detail.key}`"
              >{{ detail.text }}</span>
            </span>
          </div>
        </template>
        <template v-else>
          —
        </template>
      </div>
    </div>

    <!--
      One height, whatever is in it.

      The panel is a fixed thing on the header, not something that grows and shrinks under the
      agent: a call arriving used to make it taller, which moves every control in it - including
      Pick up - out from under the pointer at the exact moment the agent is reaching for one. So
      the room the tallest of these states needs is reserved for all of them, and the empty
      Offline panel is the same size as the one with a call on it.
    -->
    <div class="softphone-widget__state">
      <!--
        This tab inherited the softphone from one that went away. The campaign is paused and the agent
        is offered it back, rather than this tab registering on its own - see `adoptSoftphoneOwnership`.
        The offer deliberately names nothing: an agent is live for one campaign at a time, and a
        softphone campaign's name is generated, so there is nothing worth putting in front of them.
      -->
      <div
        v-if="hasSoftphoneResumePrompt"
        class="softphone-widget__resume mt-3"
        data-cy="softphone-resume-prompt"
      >
        <div class="small">
          Take calls from here?
        </div>

        <div class="d-flex mt-2">
          <b-button
            class="softphone-widget__resume-action mr-2"
            type="button"
            size="sm"
            variant="success"
            :disabled="isResuming"
            data-cy="softphone-resume"
            @click="onResume"
          >
            <b-spinner
              v-if="isResuming"
              small
            />
            <span v-else>Yes</span>
          </b-button>

          <b-button
            class="softphone-widget__resume-action"
            type="button"
            size="sm"
            variant="outline-secondary"
            :disabled="isResuming"
            data-cy="softphone-dismiss-resume"
            @click="dismissSoftphoneResumePrompt"
          >
            Not now
          </b-button>
        </div>
      </div>

      <!--
        The agent is taking calls, but in a different tab. Moving the softphone here is offered only
        while no call is up: the lock carries the transport, and WebRTC media cannot follow it, so a
        takeover mid-call would hang up on the caller.
      -->
      <div
        v-else-if="isSoftphoneInAnotherTab"
        class="softphone-widget__other-tab mt-3"
        data-cy="softphone-other-tab"
      >
        <div class="small text-muted text-center">
          {{ otherTabMessage }}
        </div>

        <b-button
          class="w-100 mt-2"
          type="button"
          size="sm"
          variant="outline-primary"
          :disabled="!canTakeOverSoftphone || isTakingOver"
          :title="takeOverTitle"
          data-cy="softphone-take-over"
          @click="onTakeOver"
        >
          <b-spinner
            v-if="isTakingOver"
            small
          />
          <span v-else>Take over here</span>
        </b-button>
      </div>

      <!--
        An agent working in another tab, or another application entirely, still gets told a call is
        here. The panel opens itself, the ringtone plays and the browser tab's own title flashes -
        see SoftphoneHeaderWidget, which owns all three.
      -->
      <div
        v-else-if="isRinging"
        class="softphone-widget__incoming mt-3 text-center"
        data-cy="softphone-incoming"
      >
        <font-awesome-icon
          :icon="['fa', 'phone-volume']"
          class="softphone-widget__incoming-icon"
        />

        <div class="softphone-widget__incoming-label mt-1">
          Incoming call
        </div>
      </div>

      <div
        v-else-if="isCallActive"
        class="text-center"
      >
        <div
          class="softphone-widget__timer mt-3"
          data-cy="softphone-call-timer"
        >
          {{ elapsedTime }}
        </div>

        <!--
          Masked until the call reaches the campaign's minimum duration - the point where the call
          becomes billable - and shown in full from the start when the campaign has no minimum. The
          last four digits are enough to confirm who is on the line until then.
        -->
        <div
          v-if="callerId"
          class="softphone-widget__caller-id mt-1"
          :class="{ 'softphone-widget__caller-id--masked': isCallerIdMasked }"
          data-cy="softphone-caller-id"
        >
          {{ callerId }}
        </div>
      </div>

      <div
        v-else-if="isWrappingUp"
        class="softphone-widget__wrap-up mt-3 text-center text-muted"
        data-cy="softphone-wrap-up"
      >
        Wrap-up · campaign paused · {{ wrapUpSecondsRemaining }}s
      </div>
    </div>

    <div
      v-if="lastError"
      class="softphone-widget__error mt-3 small text-danger text-center"
      data-cy="softphone-error"
    >
      {{ lastError }}
    </div>

    <div class="mt-auto pt-3">
      <!--
        Plain b-button rather than BaseButtonLoader: that component wraps its button in a
        b-form-group, so the disabled state and the data-cy would land on a fieldset instead of the
        control. These two buttons ARE the softphone - what they are doing has to be unambiguous.
      -->
      <div class="d-flex">
        <b-button
          class="softphone-widget__action mr-2"
          type="button"
          variant="success"
          :disabled="!canPickUp"
          data-cy="softphone-pick-up"
          @click="onPickUp"
        >
          <b-spinner
            v-if="isAnswering"
            small
          />
          <span v-else>Pick up</span>
        </b-button>

        <b-button
          class="softphone-widget__action"
          type="button"
          variant="danger"
          :disabled="!canHangUp"
          data-cy="softphone-hang-up"
          @click="onHangUp"
        >
          <b-spinner
            v-if="isHangingUp"
            small
          />
          <span v-else>Hang up</span>
        </b-button>
      </div>

      <div class="d-flex mt-2">
        <b-button
          class="softphone-widget__action mr-2"
          type="button"
          size="sm"
          variant="outline-secondary"
          :disabled="!canDecline"
          data-cy="softphone-decline"
          @click="onDecline"
        >
          Decline
        </b-button>

        <b-button
          class="softphone-widget__action"
          type="button"
          size="sm"
          variant="outline-secondary"
          :disabled="!canMute"
          data-cy="softphone-mute"
          @click="onToggleMute"
        >
          <font-awesome-icon
            :icon="['fa', isMuted ? 'microphone-slash' : 'microphone']"
            class="mr-1"
          />
          {{ isMuted ? 'Unmute' : 'Mute' }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import { showErrorMessage } from '@/notification-utils'
import { formatCallerId, maskCallerId } from '@/services/softphone/callerId'
import { SOFTPHONE_STATUS } from '@/store/modules/softphone.store'

/**
 * The category's type size, and how far down it may be taken to keep the line to one line. The
 * first matches `.softphone-widget__campaign` in the styles below - it is the size the category is
 * measured at - and the floor is the details' size: smaller than its own qualifiers would put the
 * subject second, so past this it is cut with an ellipsis instead.
 */
export const CATEGORY_FONT_SIZE_IN_REM = 1.1
export const CATEGORY_MIN_FONT_SIZE_IN_REM = 0.8

function rootFontSizeInPx() {
  const rootFontSize = typeof window !== 'undefined'
    ? parseFloat(window.getComputedStyle(document.documentElement).fontSize)
    : NaN

  return rootFontSize > 0 ? rootFontSize : 16
}

export default {
  name: 'SoftphoneWidget',

  data() {
    return {
      elapsedSeconds: 0,
      wrapUpSecondsRemaining: 0,
      isAnswering: false,
      isHangingUp: false,
      isGoingOffline: false,
      isTakingOver: false,
      isResuming: false,
      tickIntervalId: null
    }
  },

  computed: {
    ...mapState('softphone', [
      'activeCall',
      'incomingCall',
      'isMuted',
      'lastError',
      'status'
    ]),

    ...mapGetters('softphone', [
      'callerIdRevealAfterSeconds',
      'canTakeOverSoftphone',
      'hasSoftphoneResumePrompt',
      'isRinging',
      'isCallActive',
      'isSoftphoneInAnotherTab',
      'isSoftphoneOnline',
      'isSoftphoneOwner',
      'liveCampaignCategoryName',
      'liveCampaignLanguageCode',
      'liveCampaignMinimumDurationInSeconds',
      'softphoneStatusLabel'
    ]),

    /**
     * What sits beside the category: the language code and the minimum duration, each only when
     * the campaign has one.
     */
    campaignDetails() {
      const details = []

      if (this.liveCampaignLanguageCode) {
        details.push({ key: 'language', text: this.liveCampaignLanguageCode })
      }

      if (this.liveCampaignMinimumDurationInSeconds) {
        details.push({ key: 'duration', text: `${this.liveCampaignMinimumDurationInSeconds} seconds` })
      }

      return details
    },

    isWrappingUp() {
      return this.status === SOFTPHONE_STATUS.WRAP_UP
    },


    otherTabMessage() {
      return this.canTakeOverSoftphone
        ? 'The softphone is running in another tab.'
        : 'A call is in progress in another tab.'
    },

    takeOverTitle() {
      return this.canTakeOverSoftphone
        ? 'Move the softphone to this tab'
        : 'The tab on the call has to finish it first - moving the softphone would end the call'
    },

    /**
     * Only ever shown on an ANSWERED call - a ringing call deliberately shows nothing but the
     * campaign, so a number never sits on screen for a call nobody has taken.
     */
    callerId() {
      const callerNumber = this.activeCall?.callerNumber

      if (!this.isCallActive || !callerNumber) {
        return ''
      }

      return this.isCallerIdMasked ? maskCallerId(callerNumber) : formatCallerId(callerNumber)
    },

    // Masked until the campaign's minimum duration has elapsed; a campaign without one reveals the
    // number immediately, because `callerIdRevealAfterSeconds` is then 0.
    isCallerIdMasked() {
      return this.elapsedSeconds < this.callerIdRevealAfterSeconds
    },

    /**
     * Exactly the spec: pick up is live only while a call is ringing, hang up only while one is up,
     * and after the call ends neither is.
     *
     * Every control is additionally dead in a mirror. A mirror renders the owner's call faithfully -
     * that is the point of it - but the transport is in the other tab, so a button here would send
     * a request nothing is listening for.
     */
    canPickUp() {
      return this.isSoftphoneOwner && this.isRinging && !this.isAnswering
    },

    canHangUp() {
      return this.isSoftphoneOwner && this.isCallActive && !this.isHangingUp
    },

    canDecline() {
      return this.isSoftphoneOwner && this.isRinging
    },

    /**
     * Go offline is the one control a mirror keeps. It never touches the transport itself - the tab
     * that holds it is the only one that can - it asks that tab to go offline, exactly as the Go
     * Offline button on the campaigns list does from wherever it is clicked.
     *
     * Dead while there is a call on the line. Going offline pauses the campaign and drops the
     * registration, so it would end that call without ever calling itself that: Decline is how the
     * agent refuses a caller and Hang up is how they finish one.
     */
    canGoOffline() {
      return this.isSoftphoneOnline
        && !this.isRinging
        && !this.isCallActive
        && !this.isGoingOffline
    },

    goOfflineTitle() {
      if (this.isRinging) {
        return 'Decline the call first'
      }

      if (this.isCallActive) {
        return 'Hang up first'
      }

      return this.isSoftphoneOnline
        ? 'Stop taking calls and pause your live campaign'
        : 'You are not taking calls'
    },

    canMute() {
      return this.isSoftphoneOwner && this.isCallActive
    },

    // One class per status, so the badge's colour says what the label says. Offline and Other tab
    // share grey: both mean this tab is not taking calls.
    statusClass() {
      return {
        'softphone-widget__badge--connecting': this.status === SOFTPHONE_STATUS.CONNECTING,
        'softphone-widget__badge--ready': this.status === SOFTPHONE_STATUS.READY,
        'softphone-widget__badge--ringing': this.isRinging,
        'softphone-widget__badge--active': this.isCallActive,
        'softphone-widget__badge--wrap-up': this.isWrappingUp,
        'softphone-widget__badge--error': this.status === SOFTPHONE_STATUS.ERROR,
        'softphone-widget__badge--offline': this.status === SOFTPHONE_STATUS.OFFLINE
          || this.status === SOFTPHONE_STATUS.OTHER_TAB
      }
    },

    elapsedTime() {
      const minutes = Math.floor(this.elapsedSeconds / 60)
      const seconds = this.elapsedSeconds % 60

      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  },

  watch: {
    // The measurement is of rendered text, so it waits for the DOM to carry the new value.
    liveCampaignCategoryName() {
      this.$nextTick(this.fitCategory)
    },

    campaignDetails() {
      this.$nextTick(this.fitCategory)
    },

    isCallActive(isActive) {
      if (isActive) {
        this.startTimer()
      } else {
        this.stopTimer()
      }
    },

    isWrappingUp(isWrappingUp) {
      if (isWrappingUp) {
        this.startTimer()
      } else {
        this.stopTimer()
      }
    }
  },

  /**
   * The panel is mounted and unmounted as the agent collapses it, and a call can be up either way,
   * so the timer starts from whatever state is already there rather than from the next transition.
   */
  mounted() {
    if (this.isCallActive || this.isWrappingUp) {
      this.startTimer()
    }

    this.fitCategory()
    this.watchCampaignWidth()
  },

  beforeDestroy() {
    this.stopTimer()
    this.unwatchCampaignWidth()
  },

  methods: {
    /**
     * Keeps the campaign line to ONE line by setting the category smaller when it would not fit
     * beside the details - and beside the same width again on the other side, which is what keeps
     * it centred rather than merely on the line. The size falls in proportion, so the fit is found
     * in one measurement, and never below the floor: past that the category is cut with an
     * ellipsis (see the styles) and the full name is left in the title.
     *
     * Measured at the natural size every time, so a category that shrank to fit does not stay
     * small once a shorter one, or a wider panel, replaces it. A nothing-sized layout (not yet
     * attached, or a headless test) is left alone.
     */
    fitCategory() {
      const line = this.$refs.campaignLine
      const name = this.$refs.campaignName

      if (!line || !name) {
        return
      }

      name.style.fontSize = ''

      const lineWidth = line.clientWidth
      const nameWidth = name.scrollWidth

      if (!lineWidth || !nameWidth) {
        return
      }

      const details = this.$refs.campaignDetails
      const gap = parseFloat(window.getComputedStyle(line).columnGap) || 0
      const sideWidth = details ? details.offsetWidth + gap : 0
      const available = lineWidth - 2 * sideWidth

      if (nameWidth <= available) {
        return
      }

      const rootFontSize = rootFontSizeInPx()
      const naturalSize = CATEGORY_FONT_SIZE_IN_REM * rootFontSize
      const floor = CATEGORY_MIN_FONT_SIZE_IN_REM * rootFontSize
      const fitted = available > 0 ? naturalSize * (available / nameWidth) : floor

      name.style.fontSize = `${Math.max(floor, fitted)}px`
    },

    // The panel can be resized with the window; the fit follows the width it has to work with.
    watchCampaignWidth() {
      if (typeof ResizeObserver === 'undefined' || !this.$refs.campaign) {
        return
      }

      this.campaignWidthObserver = new ResizeObserver(() => this.fitCategory())
      this.campaignWidthObserver.observe(this.$refs.campaign)
    },

    unwatchCampaignWidth() {
      if (this.campaignWidthObserver) {
        this.campaignWidthObserver.disconnect()
        this.campaignWidthObserver = null
      }
    },

    ...mapActions('softphone', [
      'answerCall',
      'declineCall',
      'dismissSoftphoneResumePrompt',
      'hangUp',
      'requestGoOffline',
      'resumeSoftphoneFromPrompt',
      'setMuted',
      'takeOverSoftphone'
    ]),

    startTimer() {
      this.stopTimer()

      this.tickIntervalId = setInterval(() => {
        const answeredAt = this.activeCall?.answeredAt

        this.elapsedSeconds = answeredAt
          ? Math.max(0, Math.floor((Date.now() - answeredAt) / 1000))
          : 0

        const wrapUpEndsAt = this.$store.state.softphone.wrapUpEndsAt

        this.wrapUpSecondsRemaining = wrapUpEndsAt
          ? Math.max(0, Math.ceil((wrapUpEndsAt - Date.now()) / 1000))
          : 0
      }, 1000)
    },

    stopTimer() {
      if (this.tickIntervalId) {
        clearInterval(this.tickIntervalId)
        this.tickIntervalId = null
      }

      this.elapsedSeconds = 0
      this.wrapUpSecondsRemaining = 0
    },

    async onTakeOver() {
      try {
        this.isTakingOver = true

        const didTakeOver = await this.takeOverSoftphone()

        if (!didTakeOver) {
          showErrorMessage('Could not move the softphone to this tab')
        }
      } catch (error) {
        showErrorMessage('Could not move the softphone to this tab')
      } finally {
        this.isTakingOver = false
      }
    },

    async onResume() {
      try {
        this.isResuming = true
        await this.resumeSoftphoneFromPrompt()
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Could not go live again')
      } finally {
        this.isResuming = false
      }
    },

    async onPickUp() {
      try {
        this.isAnswering = true
        await this.answerCall()
      } catch (error) {
        showErrorMessage('Could not answer the call')
      } finally {
        this.isAnswering = false
      }
    },

    async onDecline() {
      try {
        await this.declineCall()
      } catch (error) {
        showErrorMessage('Could not decline the call')
      }
    },

    async onHangUp() {
      try {
        this.isHangingUp = true
        await this.hangUp()
      } catch (error) {
        showErrorMessage('Could not hang up the call')
      } finally {
        this.isHangingUp = false
      }
    },

    /**
     * Same failure message as the campaigns list, and the same silence on success: the status badge
     * and the button itself both say what happened, where the agent is already looking.
     *
     * The panel that hosts this widget closes behind a successful click - see the header. Going
     * offline is the agent saying they are done with the softphone for now, and leaving the panel
     * open in front of them says the opposite. Only on success: a panel that closed over a failure
     * would take the error message with it.
     */
    async onGoOffline() {
      try {
        this.isGoingOffline = true
        await this.requestGoOffline()

        this.$emit('went-offline')
      } catch (error) {
        showErrorMessage('Could not take you offline')
      } finally {
        this.isGoingOffline = false
      }
    },

    async onToggleMute() {
      try {
        await this.setMuted(!this.isMuted)
      } catch (error) {
        showErrorMessage('Could not change the microphone')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.softphone-widget {
  flex: 1;
}

/*
 * Every control is sized from zero rather than from its own label, so each row is two equal halves
 * and the halves line up down the panel - whatever the buttons in them happen to say. Mute relabels
 * itself to Unmute under the agent, and a control that resizes as it relabels moves the one beside
 * it out from under the pointer.
 */
.softphone-widget__action {
  flex: 1 1 0;
  min-width: 0;
}

/*
 * Room for the tallest state the panel can be in - the timer with a caller id under it - held
 * whatever state it is actually in, so nothing below this moves when a call arrives or ends. Worth
 * measuring again if anything is added to one of those states.
 */
.softphone-widget__state {
  min-height: 5.5rem;
}

/* Tall enough for the button hung inside it, so the campaign line below keeps its distance. */
.softphone-widget__status {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
}

.softphone-widget__go-offline {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.softphone-widget__badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.softphone-widget__badge--connecting {
  color: #fff;
  background-color: #fd7e14;
}

.softphone-widget__badge--ready {
  color: #fff;
  background-color: #28a745;
}

/* Dark text on both of the pale badges: white on either is unreadable. */
.softphone-widget__badge--ringing {
  color: #084298;
  background-color: #cfe2ff;
}

/* The one badge the same colour as the panel behind it, so it carries a border. */
.softphone-widget__badge--active {
  color: #212529;
  background-color: #fff;
  box-shadow: inset 0 0 0 1px #ced4da;
}

.softphone-widget__badge--wrap-up {
  color: #212529;
  background-color: #ffc107;
}

.softphone-widget__badge--error {
  color: #fff;
  background-color: #dc3545;
}

.softphone-widget__badge--offline {
  color: #fff;
  background-color: #6c757d;
}

/**
 * Equal halves, whatever the labels are. `flex-fill` sizes from the content, so "Yes" came out
 * narrower than "Not now" - and an offer whose two answers are different sizes reads as though one
 * of them is the real one.
 */
.softphone-widget__resume-action {
  flex: 1 1 0;
}

.softphone-widget__campaign {
  font-size: 1.1rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

// Three columns with the category in the middle one, sized to itself, between two equal sides:
// that is what centres it alone, as if the details were not there. The details live in the right
// side, and `fitCategory` sets the category small enough to leave that much room on BOTH sides,
// so the two stay equal. Only past the smallest size it may be set does centring give way: the
// right side then holds its ground for the details, the left gives way to nothing, and the
// category is cut with an ellipsis. `align-items: baseline` sets the smaller type on the
// category's baseline rather than floating it mid-height.
.softphone-widget__campaign-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, auto) minmax(max-content, 1fr);
  column-gap: 0.75rem;
  align-items: baseline;
}

.softphone-widget__campaign-name {
  grid-column: 2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// The language and the minimum duration: present, but second to the category. A campaign
// without a minimum adds nothing here - there is no threshold to watch, and saying so would put
// a term on screen that never changes.
.softphone-widget__campaign-details {
  grid-column: 3;
  display: flex;
  gap: 0.5rem;
  justify-self: start;
  white-space: nowrap;
}

.softphone-widget__campaign-detail {
  font-size: 0.8rem;
  font-weight: 400;
}

.softphone-widget__incoming-icon {
  font-size: 2rem;
  animation: softphone-pulse 1s ease-in-out infinite;
}

.softphone-widget__incoming-label {
  font-weight: 600;
}

.softphone-widget__timer {
  font-size: 1.75rem;
  font-variant-numeric: tabular-nums;
}

.softphone-widget__caller-id {
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.softphone-widget__caller-id--masked {
  color: #adb5bd;
}

@keyframes softphone-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
